import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, useEffect, useRef } from "react";
// `lazy` is aliased to our retry-then-reload wrapper. Every `lazy(() =>
// import(...))` call below survives a stale-deploy chunk 404 instead of
// going to a blank screen. See lib/lazyWithRetry.ts.
import { lazyWithRetry as lazy } from "./lib/lazyWithRetry";
import { TRADES } from "../lib/trades";
import { CITIES } from "../lib/cities";
import { STATES } from "../lib/states";
import { trackPageview } from "./lib/track";
import { useSession, useCrewMode } from "./lib/useSession";
import { RequirePro } from "./components/RequirePro";
import { isNative } from "./lib/native";

// In the Capacitor iOS/Android shell, "/" is almost never the right
// destination — visitors who installed the native app already know what
// FieldKrew is and just want to log in. Show the Landing page on web,
// route straight to /signin on native. The Landing page is still
// reachable via deep link if anyone wants it.
function RootRoute() {
  return isNative() ? <Navigate to="/signin" replace /> : <Landing />;
}

const Landing = lazy(() => import("./pages/Landing"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Pay = lazy(() => import("./pages/Pay"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Calendar = lazy(() => import("./pages/Calendar"));
const CrewMap = lazy(() => import("./pages/CrewMap"));
const Gantt = lazy(() => import("./pages/Gantt"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Quotes = lazy(() => import("./pages/Quotes"));
const QuoteEdit = lazy(() => import("./pages/QuoteEdit"));
const LeadIntake = lazy(() => import("./pages/LeadIntake"));
const QuoteDetail = lazy(() => import("./pages/QuoteDetail"));
const QuoteView = lazy(() => import("./pages/QuoteView"));
const BidPage = lazy(() => import("./pages/BidPage"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const PortalJobDetail = lazy(() =>
  import("./pages/CustomerPortal").then((m) => ({ default: m.PortalJobDetail })),
);
const PortalDashboard = lazy(() => import("./pages/PortalDashboard"));
const PortalLogin = lazy(() => import("./pages/PortalLogin"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const ChangeOrderView = lazy(() => import("./pages/ChangeOrderView"));
const SignoffView = lazy(() => import("./pages/SignoffView"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const LeadCapture = lazy(() => import("./pages/LeadCapture"));
const About = lazy(() => import("./pages/About"));
const Compare = lazy(() => import("./pages/Compare"));
const CompareAll = lazy(() => import("./pages/CompareAll"));
const Roofers = lazy(() => import("./pages/Roofers"));
const TradePage = lazy(() => import("./pages/TradePage"));
const GeoTradePage = lazy(() => import("./pages/GeoTradePage"));
const QuoteTool = lazy(() => import("./pages/QuoteTool"));
const SetupGuides = lazy(() => import("./pages/SetupGuides"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryTerm = lazy(() => import("./pages/GlossaryTerm"));
const Guides = lazy(() => import("./pages/Guides"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const ContractorStudy = lazy(() => import("./pages/ContractorStudy"));
const Tools = lazy(() => import("./pages/Tools"));
const RoofingCalculator = lazy(() => import("./pages/RoofingCalculator"));
const MarkupCalculator = lazy(() => import("./pages/MarkupCalculator"));
const Contacts = lazy(() => import("./pages/Contacts"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Resubscribe = lazy(() => import("./pages/Resubscribe"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EmployeeHome = lazy(() => import("./pages/EmployeeHome"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const Jobs = lazy(() => import("./pages/Jobs"));
const ImportData = lazy(() => import("./pages/ImportData"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Subs = lazy(() => import("./pages/Subs"));
const SubDetail = lazy(() => import("./pages/SubDetail"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Setup = lazy(() => import("./pages/Setup"));
const Parts = lazy(() => import("./pages/Parts"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Suppliers = lazy(() => import("./pages/Suppliers"));
const Commissions = lazy(() => import("./pages/Commissions"));
const Safety = lazy(() => import("./pages/Safety"));
const Fleet = lazy(() => import("./pages/Fleet"));
const Forms = lazy(() => import("./pages/Forms"));
const ReportBuilder = lazy(() => import("./pages/ReportBuilder"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Reports = lazy(() => import("./pages/Reports"));
const Mileage = lazy(() => import("./pages/Mileage"));
const Warranties = lazy(() => import("./pages/Warranties"));
const TimeOff = lazy(() => import("./pages/TimeOff"));
const Employees = lazy(() => import("./pages/Employees"));
const EmployeeDetail = lazy(() => import("./pages/EmployeeDetail"));
const Messages = lazy(() => import("./pages/Messages"));
const QuoteTemplates = lazy(() => import("./pages/QuoteTemplates"));
const Support = lazy(() => import("./pages/Support"));
const AdminOverview = lazy(() => import("./pages/admin/Overview"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminUserDetail = lazy(() => import("./pages/admin/UserDetail"));
const AdminLog = lazy(() => import("./pages/admin/Log"));
const AdminAuditLog = lazy(() => import("./pages/admin/AuditLog"));
const AdminSupport = lazy(() => import("./pages/admin/Support"));
const AdminInbox = lazy(() => import("./pages/admin/Inbox"));
const AdminFinancial = lazy(() => import("./pages/admin/Financial"));
const AdminDemo = lazy(() => import("./pages/admin/Demo"));
const AdminWeb = lazy(() => import("./pages/admin/Web"));
const AdminHealth = lazy(() => import("./pages/admin/Health"));
const AdminOps = lazy(() => import("./pages/admin/Ops"));
const AdminTwilioHybrid = lazy(() => import("./pages/admin/TwilioHybrid"));
const AdminSandbox = lazy(() => import("./pages/admin/Sandbox"));
const AdminCapacity = lazy(() => import("./pages/admin/Capacity"));
const AdminReferrals = lazy(() => import("./pages/admin/Referrals"));
const AdminProWaitlist = lazy(() => import("./pages/admin/ProWaitlist"));
const AdminQuoteToolUsage = lazy(() => import("./pages/admin/QuoteToolUsage"));
const AdminGrowthFunnels = lazy(() => import("./pages/admin/GrowthFunnels"));
const WebsiteBuilder = lazy(() => import("./pages/WebsiteBuilder"));
const AdminAccounts = lazy(() => import("./pages/admin/Accounts"));
const HelpPage = lazy(() => import("./pages/Help"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ProSetup = lazy(() => import("./pages/ProSetup"));
const PaymentVerification = lazy(() => import("./pages/PaymentVerification"));
const AdminVerifications = lazy(() => import("./pages/admin/Verifications"));
const AdminSEO = lazy(() => import("./pages/admin/SEO"));
const AdminBlog = lazy(() => import("./pages/admin/BlogAdmin"));
const AdminFinancialMetrics = lazy(() => import("./pages/admin/FinancialMetrics"));
const AdminChangelog = lazy(() => import("./pages/admin/ChangelogAdmin"));
const AdminRoadmap = lazy(() => import("./pages/admin/RoadmapAdmin"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Security = lazy(() => import("./pages/Security"));
const StatusPage = lazy(() => import("./pages/Status"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const WhyFieldKrew = lazy(() => import("./pages/WhyFieldKrew"));
const Imports = lazy(() => import("./pages/Imports"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SwitchIndex = lazy(() => import("./pages/SwitchIndex"));
const SwitchGuide = lazy(() => import("./pages/SwitchGuide"));
const SubPortal = lazy(() => import("./pages/SubPortal"));
// Persistent-login sub portal (migration 104). The same module exports
// three components (login / accept-invite / dashboard); we lazy each as
// its own chunk-cheap entry point so the bundle doesn't pay for the
// dashboard on the login page.
const SubLogin = lazy(() => import("./pages/SubHome").then((m) => ({ default: m.SubLogin })));
const SubAcceptInvite = lazy(() => import("./pages/SubHome").then((m) => ({ default: m.SubAcceptInvite })));
const SubDashboard = lazy(() => import("./pages/SubHome").then((m) => ({ default: m.SubDashboard })));

function Loading() {
  return (
    <div className="flex h-screen items-center justify-center text-navy-500">
      Loading…
    </div>
  );
}

// Pick the right /app home screen based on the session's account_role.
// Owners + account admins → standard business Dashboard.
// Employees → stripped-down clock-in/out Home (no business data).
// Privileged accounts force-locked via the "Business home" permission
// toggle → same EmployeeHome treatment, no escape.
function HomeForRole() {
  const { user, loading } = useSession();
  const [crewMode] = useCrewMode();
  if (loading) return <Loading />;
  // Hard lock: an owner can take the business dashboard away from a
  // privileged account by unchecking "Business home" on EmployeeDetail.
  // That account is then stuck on EmployeeHome regardless of crewMode.
  const isTeam = !!user?.parent_account_id;
  const isAccountAdmin = isTeam && user.account_role === "admin";
  const forcedKrewView =
    isAccountAdmin &&
    user.team_permissions_set &&
    !user.team_permissions.includes("business_home");
  // Crew mode lets an owner / privileged user opt into the field-crew
  // home for on-site work. Employees always land on EmployeeHome.
  if (user?.account_role === "employee" || crewMode || forcedKrewView)
    return <EmployeeHome />;
  return <Dashboard />;
}

// Fires a pageview every time the SPA route changes. Sits inside the
// Router subtree so useLocation works. Deduplicates back-to-back fires of
// the same path (e.g. StrictMode double-invokes) so we don't double-count.
function PageviewTracker() {
  const location = useLocation();
  const lastSent = useRef<string | null>(null);
  useEffect(() => {
    const full = location.pathname + location.search;
    if (lastSent.current === full) return;
    lastSent.current = full;
    trackPageview(full);
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <PageviewTracker />
      <Routes>
        {/* Public */}
        <Route path="/" element={<RootRoute />} />
        {/* Branded partner-referral landing — `/p/:code` 302s here after
            dropping the fk_partner cookie. See Welcome.tsx + public-partner.ts. */}
        <Route path="/welcome/:code" element={<Welcome />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pay/:id" element={<Pay />} />
        <Route path="/pay/:id/done" element={<Pay />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/q/:token" element={<QuoteView />} />
        <Route path="/bid/:token" element={<BidPage />} />
        <Route path="/c/:token" element={<CustomerPortal />} />
        <Route path="/c/:token/jobs/:jobId" element={<PortalJobDetail />} />
        <Route path="/portal/dashboard" element={<PortalDashboard />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/r/:slug" element={<ReviewPage />} />
        <Route path="/co/:token" element={<ChangeOrderView />} />
        <Route path="/signoff/:token" element={<SignoffView />} />
        <Route path="/s/:token" element={<SubPortal />} />
        <Route path="/sub/login" element={<SubLogin />} />
        <Route path="/sub/invite/:token" element={<SubAcceptInvite />} />
        <Route path="/sub" element={<SubDashboard />} />
        <Route path="/gallery/:token" element={<GalleryPage />} />
        <Route path="/lead/:slug" element={<LeadCapture />} />
        <Route path="/compare" element={<CompareAll />} />
        <Route path="/compare/:slug" element={<Compare />} />
        <Route path="/alternatives/:slug" element={<Alternatives />} />
        <Route path="/why-fieldkrew" element={<WhyFieldKrew />} />
        <Route path="/imports" element={<Imports />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/switch" element={<SwitchIndex />} />
        <Route path="/switch/:slug" element={<SwitchGuide />} />
        {/* Legacy URL preserved for backlinks — redirect to the new slug. */}
        <Route path="/switch-from-jobber" element={<Navigate to="/switch/jobber" replace />} />
        <Route path="/roofers" element={<Roofers />} />
        {TRADES.map((t) => (
          <Route key={t.slug} path={`/${t.slug}-crm`} element={<TradePage />} />
        ))}
        {/* Geo × trade long-tail pages — one Route per (trade, city)
            pair, generated from TRADES × CITIES. Listing them
            explicitly (rather than one parameterized route) makes the
            registration order deterministic and keeps each path
            unambiguous in the router so deep links from search land
            on GeoTradePage cleanly. */}
        {TRADES.flatMap((t) =>
          CITIES.map((c) => (
            <Route
              key={`${t.slug}-${c.slug}`}
              path={`/${t.slug}-crm/${c.slug}`}
              element={<GeoTradePage />}
            />
          )),
        )}
        {/* State-level rollup pages — /{trade}-crm/{state-slug}.
            Registered AFTER the city routes so a city slug like
            "san-antonio-tx" never shadows a matching state slug (none
            currently do, but explicit ordering keeps it safe). Same
            GeoTradePage component handles both — it inspects the last
            path segment and reads from STATES vs CITIES accordingly. */}
        {TRADES.flatMap((t) =>
          STATES.map((s) => (
            <Route
              key={`${t.slug}-state-${s.slug}`}
              path={`/${t.slug}-crm/${s.slug}`}
              element={<GeoTradePage />}
            />
          )),
        )}
        <Route path="/guides" element={<Guides />} />
        <Route
          path="/research/california-contractor-report"
          element={<ContractorStudy />}
        />
        {/* Single parameterized route — GuidePage looks the slug up
            against GUIDE_INDEX and Navigate's back to /guides if it
            doesn't match. Avoids the failure mode where the previous
            `{GUIDES.map(...)}` route fan-out registers zero routes if
            GUIDES is empty at mount time (build/bundle race). */}
        <Route path="/guides/:slug" element={<GuidePage />} />
        {/* Glossary — A-Z index + one parameterized per-term route.
            Same robust shape as the guides routes. */}
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/glossary/:slug" element={<GlossaryTerm />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/tools/roofing-calculator" element={<RoofingCalculator />} />
        <Route path="/tools/markup-margin-calculator" element={<MarkupCalculator />} />

        {/* Auth required */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/resubscribe" element={<Resubscribe />} />
        <Route path="/app" element={<HomeForRole />} />
        <Route path="/app/setup" element={<Setup />} />
        {/* Free public quote tool — no signup, PDF download,
            branded with the visitor's logo + color. Lead magnet. */}
        <Route path="/quote-tool" element={<QuoteTool />} />
        <Route path="/free-quote-generator" element={<QuoteTool />} />
        <Route path="/contractor-quote-tool" element={<QuoteTool />} />
        {/* Public setup guides for all three tiers. */}
        <Route path="/setup" element={<SetupGuides />} />
        <Route path="/setup/:tier" element={<SetupGuides />} />
        <Route path="/app/leads" element={<Leads />} />
        <Route path="/app/leads/:id" element={<LeadDetail />} />
        {/* Open lead-intake form — accessible to employees + privileged.
            The list/detail pages on /app/leads stay admin-only via the
            requireOwner gate; this is a write-only capture surface. */}
        <Route path="/app/lead-new" element={<LeadIntake />} />
        <Route path="/app/jobs" element={<Jobs />} />
        <Route path="/app/import" element={<ImportData />} />
        <Route path="/app/jobs/:id" element={<JobDetail />} />
        {/* v2 pricing: only Subs, Website, and Employees are <RequirePro>.
            Everything else (customers, invoices, calendar, quotes, parts,
            reports, warranties, messages, quote-templates) is Free. Pro
            gates live elsewhere: photo/plan uploads (file-storage), auto
            outreach/lead-sources/recurring/automations (server-side
            scheduling), email quota cap. */}
        <Route path="/app/subs" element={<RequirePro><Subs /></RequirePro>} />
        <Route path="/app/subs/:id" element={<RequirePro><SubDetail /></RequirePro>} />
        <Route path="/app/customers" element={<Customers />} />
        <Route path="/app/customers/:key" element={<CustomerDetail />} />
        <Route path="/app/contacts" element={<Contacts />} />
        <Route path="/app/contacts/:id" element={<ContactDetail />} />
        <Route path="/app/invoices" element={<Invoices />} />
        <Route path="/app/calendar" element={<Calendar />} />
        <Route path="/app/gantt" element={<Gantt />} />
        <Route path="/app/crew-map" element={<RequirePro><CrewMap /></RequirePro>} />
        <Route path="/app/quotes" element={<Quotes />} />
        <Route path="/app/quotes/new" element={<QuoteEdit />} />
        <Route path="/app/quotes/:id" element={<QuoteDetail />} />
        <Route path="/app/quotes/:id/edit" element={<QuoteEdit />} />
        <Route path="/app/settings" element={<Settings />} />
        <Route path="/app/website" element={<RequirePro><WebsiteBuilder /></RequirePro>} />
        <Route path="/app/parts" element={<Parts />} />
        {/* Path aliases. `/app/pricebook` is the marketing / nav name for
            the same thing the code calls "parts" — bookmarks, links from
            emails, and typed URLs land here and continue to the real page
            instead of being dumped on the marketing homepage. */}
        <Route path="/app/pricebook" element={<Navigate to="/app/parts" replace />} />
        <Route path="/app/inventory" element={<RequirePro><Inventory /></RequirePro>} />
        <Route path="/app/suppliers" element={<RequirePro><Suppliers /></RequirePro>} />
        <Route path="/app/commissions" element={<RequirePro><Commissions /></RequirePro>} />
        <Route path="/app/safety" element={<RequirePro><Safety /></RequirePro>} />
        <Route path="/app/fleet" element={<RequirePro><Fleet /></RequirePro>} />
        <Route path="/app/fleet/:id" element={<RequirePro><Fleet /></RequirePro>} />
        <Route path="/app/forms" element={<Forms />} />
        <Route path="/app/report-builder" element={<RequirePro><ReportBuilder /></RequirePro>} />
        <Route path="/app/expenses" element={<Expenses />} />
        <Route path="/app/reports" element={<Reports />} />
        <Route path="/app/mileage" element={<Mileage />} />
        {/* Warranties roll-up + cron-driven expiry alerts are Pro in v3. */}
        <Route path="/app/warranties" element={<RequirePro><Warranties /></RequirePro>} />
        <Route path="/app/time-off" element={<RequirePro><TimeOff /></RequirePro>} />
        <Route path="/app/employees" element={<RequirePro><Employees /></RequirePro>} />
        <Route path="/app/employees/:id" element={<RequirePro><EmployeeDetail /></RequirePro>} />
        <Route path="/app/messages" element={<Messages />} />
        <Route path="/app/quote-templates" element={<QuoteTemplates />} />
        <Route path="/app/support" element={<Support />} />
        <Route path="/app/support/tickets/:number" element={<Support />} />
        {/* Blog — DB-backed. /blog lists live posts, /blog/:slug renders
            one. Worker injects per-route SEO head (BlogPosting +
            FAQPage + BreadcrumbList JSON-LD) so crawlers get metadata
            without executing React. */}
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/help" element={<HelpPage />} />
        {/* Public per-article help URL — same component, uses the URL
            slug to auto-open a specific article. Indexable by search
            (see the HELP_CORPUS routes in src/lib/seo.ts) so the 33
            help articles become 33 addressable content pages instead of
            hash fragments on a single SPA. */}
        <Route path="/help/:slug" element={<HelpPage />} />
        <Route path="/app/pro-setup" element={<ProSetup />} />
        <Route path="/app/verify" element={<PaymentVerification />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:id" element={<AdminUserDetail />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/inbox" element={<AdminInbox />} />
        <Route path="/admin/log" element={<AdminLog />} />
        <Route path="/admin/audit-log" element={<AdminAuditLog />} />
        <Route path="/admin/financial" element={<AdminFinancial />} />
        <Route path="/admin/demo" element={<AdminDemo />} />
        <Route path="/admin/web" element={<AdminWeb />} />
        <Route path="/admin/health" element={<AdminHealth />} />
        <Route path="/admin/ops" element={<AdminOps />} />
        <Route path="/admin/twilio-hybrid" element={<AdminTwilioHybrid />} />
        <Route path="/admin/sandbox" element={<AdminSandbox />} />
        <Route path="/admin/capacity" element={<AdminCapacity />} />
        <Route path="/admin/referrals" element={<AdminReferrals />} />
        <Route path="/admin/pro-waitlist" element={<AdminProWaitlist />} />
        <Route path="/admin/quote-tool-usage" element={<AdminQuoteToolUsage />} />
        <Route path="/admin/growth" element={<AdminGrowthFunnels />} />
        <Route path="/admin/verifications" element={<AdminVerifications />} />
        <Route path="/admin/seo" element={<AdminSEO />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/financial-metrics" element={<AdminFinancialMetrics />} />
        <Route path="/admin/changelog" element={<AdminChangelog />} />
        <Route path="/admin/roadmap" element={<AdminRoadmap />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/security" element={<Security />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/admin/accounts" element={<AdminAccounts />} />

        {/* An unknown `/app/*` path is a signed-in-user typo (bad
            bookmark, stale email link, dropped nav item). Kicking them to
            the marketing homepage is jarring — they're already inside
            the app. Land them on their dashboard instead. Everything
            else still falls through to the marketing site. */}
        <Route path="/app/*" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
